import React, { useEffect, useState } from "react";
import { ShieldCheck, Plus, Trash2, GripVertical, X } from "lucide-react";
import { toast } from "sonner";

import { v2Api } from "@/lib/v2Api";
import { confirm } from "@/lib/confirm";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const ENTITY_TYPES = [
  { value: "quotation", label: "Quotation" },
  { value: "bom", label: "BOM" },
  { value: "bid_decision", label: "Bid decision (Go / No-Go)" },
  // Sprint #5: BID_ARTIFACT enum value added in migration 20260524dddd.
  // Lets admins define threshold-based policies for artifact approvals
  // (e.g. "quotation > 50L needs CFO + Director chain") that override
  // the ad-hoc chain spawned by /v2/artifact-approver-roles.
  { value: "bid_artifact", label: "Bid artifact (quotation / proposal / compliance / …)" },
  { value: "purchase_order", label: "Purchase order" },
  { value: "discount_override", label: "Discount override" },
];
const OPERATORS = [
  { value: "gte", label: "≥ (greater than or equal)" },
  { value: "lte", label: "≤ (less than or equal)" },
  { value: "eq", label: "= (equal to)" },
  { value: "neq", label: "≠ (not equal)" },
  { value: "between", label: "between (low–high)" },
  { value: "always", label: "always (no condition)" },
];
const MODES = [
  { value: "sequential", label: "Sequential — each role in order" },
  { value: "parallel", label: "Parallel — all roles together" },
  { value: "any_of", label: "Any one of — first to decide wins" },
];

function PolicyDialog({ open, onOpenChange, initial, onSaved, allRoles }) {
  const blank = () => ({
    name: "", entity_type: "quotation", trigger_field: "grand_total",
    trigger_operator: "gte", trigger_value: "", trigger_value2: "",
    trigger_currency: "INR", required_role_codes: [],
    mode: "sequential", priority: 100, is_active: true,
  });
  const [form, setForm] = useState(blank());
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) setForm(initial ? { ...blank(), ...initial,
      trigger_value: initial.trigger_value ?? "",
      trigger_value2: initial.trigger_value2 ?? ""
    } : blank());
  }, [open, initial]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    if (!form.required_role_codes.length) {
      toast.error("Pick at least one approver role"); return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        trigger_value: form.trigger_value === "" ? null : Number(form.trigger_value),
        trigger_value2: form.trigger_value2 === "" ? null : Number(form.trigger_value2),
        priority: Number(form.priority) || 100,
      };
      if (initial?.id) await v2Api.approvals.patchPolicy(initial.id, payload);
      else await v2Api.approvals.createPolicy(payload);
      toast.success(initial?.id ? "Policy updated" : "Policy created");
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail ?? "Save failed");
    } finally { setBusy(false); }
  };

  const moveRole = (i, dir) => {
    const next = [...form.required_role_codes];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    setForm({ ...form, required_role_codes: next });
  };
  const addRole = (code) => {
    if (!code || form.required_role_codes.includes(code)) return;
    setForm({ ...form, required_role_codes: [...form.required_role_codes, code] });
  };
  const removeRole = (i) => {
    const next = form.required_role_codes.filter((_, idx) => idx !== i);
    setForm({ ...form, required_role_codes: next });
  };

  const showValueFields = !["always"].includes(form.trigger_operator);
  const showValue2 = form.trigger_operator === "between";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{initial?.id ? "Edit policy" : "New approval policy"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2 text-sm">
          <div>
            <Label className="text-xs text-slate-500">Name</Label>
            <Input value={form.name} onChange={set("name")}
                   placeholder="e.g. Quotation > 1 Cr needs CXO sign-off" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs text-slate-500">Entity type</Label>
              <select value={form.entity_type} onChange={set("entity_type")}
                      className="w-full px-2 py-1 border rounded-md bg-white text-sm">
                {ENTITY_TYPES.map((e) => <option key={e.value} value={e.value}>{e.label}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Priority (lower wins)</Label>
              <Input type="number" value={form.priority} onChange={set("priority")} />
            </div>
          </div>

          <div className="border rounded-md p-2 space-y-2">
            <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">
              Trigger condition
            </p>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs text-slate-500">Field</Label>
                <Input value={form.trigger_field} onChange={set("trigger_field")}
                       placeholder="grand_total / discount_pct / margin_pct" />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Operator</Label>
                <select value={form.trigger_operator} onChange={set("trigger_operator")}
                        className="w-full px-2 py-1 border rounded-md bg-white text-sm">
                  {OPERATORS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              {showValueFields && (
                <div>
                  <Label className="text-xs text-slate-500">Value</Label>
                  <Input type="number" value={form.trigger_value} onChange={set("trigger_value")}
                         placeholder="e.g. 10000000" />
                </div>
              )}
              {showValue2 && (
                <div>
                  <Label className="text-xs text-slate-500">Value 2 (high)</Label>
                  <Input type="number" value={form.trigger_value2} onChange={set("trigger_value2")} />
                </div>
              )}
              <div>
                <Label className="text-xs text-slate-500">Currency</Label>
                <Input value={form.trigger_currency} onChange={set("trigger_currency")}
                       placeholder="INR" />
              </div>
            </div>
          </div>

          <div>
            <Label className="text-xs text-slate-500">Approver roles (in order)</Label>
            <div className="space-y-1 mt-1">
              {form.required_role_codes.length === 0 && (
                <p className="text-[10px] italic text-slate-400">No approvers — add at least one below.</p>
              )}
              {form.required_role_codes.map((code, i) => (
                <div key={`${code}-${i}`} className="flex items-center gap-1 bg-slate-50 border rounded px-2 py-1">
                  <GripVertical className="w-3 h-3 text-slate-400" />
                  <span className="font-mono text-xs flex-1">{code}</span>
                  <button onClick={() => moveRole(i, -1)} disabled={i === 0}
                          className="text-slate-400 hover:text-blue-600 disabled:text-slate-200 text-xs">↑</button>
                  <button onClick={() => moveRole(i, 1)} disabled={i === form.required_role_codes.length - 1}
                          className="text-slate-400 hover:text-blue-600 disabled:text-slate-200 text-xs">↓</button>
                  <button onClick={() => removeRole(i)}
                          className="text-slate-300 hover:text-rose-500">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
            <select onChange={(e) => { addRole(e.target.value); e.target.value = ""; }}
                    className="w-full px-2 py-1 border rounded-md bg-white text-sm mt-1">
              <option value="">+ add approver role…</option>
              {allRoles
                .filter((r) => !form.required_role_codes.includes(r.code))
                .map((r) => <option key={r.id} value={r.code}>{r.name} ({r.code})</option>)}
            </select>
          </div>

          <div>
            <Label className="text-xs text-slate-500">Mode</Label>
            <select value={form.mode} onChange={set("mode")}
                    className="w-full px-2 py-1 border rounded-md bg-white text-sm">
              {MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy}>
            {busy ? "Saving…" : initial?.id ? "Save" : "Create policy"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ApprovalPoliciesPage() {
  const [policies, setPolicies] = useState([]);
  const [allRoles, setAllRoles] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try {
      const [p, r] = await Promise.all([
        v2Api.approvals.policies(),
        v2Api.roles().catch(() => []),
      ]);
      setPolicies(p || []);
      setAllRoles(r || []);
    } catch { toast.error("Could not load policies"); }
    finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  const remove = async (p) => {
    if (!await confirm(`Delete policy "${p.name}"?`, { title: "Delete policy", confirmLabel: "Delete" })) return;
    try { await v2Api.approvals.deletePolicy(p.id); refresh(); }
    catch { toast.error("Delete failed"); }
  };
  const toggleActive = async (p) => {
    try { await v2Api.approvals.patchPolicy(p.id, { is_active: !p.is_active }); refresh(); }
    catch { toast.error("Update failed"); }
  };
  const fmt = (p) => {
    if (p.trigger_operator === "always") return "always";
    const v = p.trigger_value;
    const v2 = p.trigger_value2;
    const cur = p.trigger_currency || "";
    const opLabel = { gte: "≥", lte: "≤", eq: "=", neq: "≠", between: "between" }[p.trigger_operator] || p.trigger_operator;
    if (p.trigger_operator === "between") return `${p.trigger_field} ${opLabel} ${v}–${v2} ${cur}`;
    return `${p.trigger_field} ${opLabel} ${new Intl.NumberFormat("en-IN").format(v)} ${cur}`;
  };

  return (
    <div className="p-8 max-w-5xl mx-auto space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-blue-600" /> Approval Policies
          </h1>
          <p className="text-sm text-slate-500">
            Author Maker / Checker / CXO chains. When an entity (quotation, BOM,
            bid decision…) hits a policy's trigger condition, an ApprovalChain
            is auto-instantiated with one step per required role. Lower priority
            wins on tie.
          </p>
        </div>
        <Button onClick={() => { setEditing(null); setOpen(true); }}>
          <Plus className="w-4 h-4 mr-1" /> New policy
        </Button>
      </div>

      {loading && <p className="text-sm text-slate-500">Loading…</p>}

      {!loading && policies.length === 0 && (
        <Card><CardContent className="py-10 text-center text-slate-400">
          No policies yet. The default is "no approval needed". Add one to start
          enforcing sign-off chains.
        </CardContent></Card>
      )}

      <div className="space-y-2">
        {policies.map((p) => (
          <Card key={p.id} className={p.is_active ? "" : "opacity-60"}>
            <CardContent className="py-3 flex items-center justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-semibold text-slate-800">{p.name}</p>
                  <Badge variant="outline" className="text-[10px]">{p.entity_type}</Badge>
                  <Badge className="bg-blue-50 text-blue-700 border-blue-200 text-[10px]">{p.mode}</Badge>
                  <span className="text-[10px] text-slate-400">priority {p.priority}</span>
                </div>
                <p className="text-xs text-slate-600 font-mono mt-1">{fmt(p)}</p>
                <div className="flex flex-wrap gap-1 mt-1">
                  {(p.required_role_codes || []).map((code, i) => (
                    <Badge key={`${code}-${i}`} variant="outline"
                           className="text-[10px] bg-slate-50">
                      {i + 1}. {code}
                    </Badge>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button onClick={() => toggleActive(p)}
                        className={`text-[10px] px-2 py-0.5 rounded-full border ${
                          p.is_active
                            ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                            : "bg-slate-100 text-slate-500 border-slate-200"
                        }`}>
                  {p.is_active ? "active" : "inactive"}
                </button>
                <button onClick={() => { setEditing(p); setOpen(true); }}
                        className="text-slate-300 hover:text-blue-500 text-xs underline">
                  edit
                </button>
                <button onClick={() => remove(p)} className="text-slate-300 hover:text-rose-500">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <PolicyDialog
        open={open}
        onOpenChange={setOpen}
        initial={editing}
        allRoles={allRoles}
        onSaved={refresh}
      />
    </div>
  );
}
